import React from 'react';
import { Trash2, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { TrustBadge } from '../components/ui/TrustBadge';
import { useCart } from '../hooks/useCart';
import { motion, AnimatePresence } from 'framer-motion';
export function CartPage() {
  const {
    items,
    updateQuantity,
    removeFromCart,
    subtotal,
    ebtTotal
  } = useCart();
  const deliveryFee = subtotal > 35 ? 0 : 5.99;
  const total = subtotal + deliveryFee;
  return <div className="min-h-screen bg-[#F8F9FA]" data-id="element-264">
      <EBTWICBanner data-id="element-265" />
      <TopNav data-id="element-266" />

      <main className="max-w-7xl mx-auto px-6 py-12" data-id="element-267">
        <div className="mb-8" data-id="element-268">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4" data-id="element-269">
            <ArrowLeft className="w-5 h-5" data-id="element-270" />
            <span className="font-medium" data-id="element-271">Continue Shopping</span>
          </Link>
          <h1 className="text-4xl font-bold text-gray-900" data-id="element-272">Your Cart</h1>
        </div>

        {items.length > 0 ? <div className="grid lg:grid-cols-3 gap-8" data-id="element-273">
            <div className="lg:col-span-2 space-y-4" data-id="element-274">
              <AnimatePresence initial={false} data-id="element-275">
                {items.map(item => <motion.div key={item.id} layout initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex gap-6" data-id="element-276">
                    <div className="w-32 h-32 bg-gray-50 rounded-xl flex-shrink-0 overflow-hidden" data-id="element-277">
                      <img src={item.image} alt="" className="w-full h-full object-cover" data-id="element-278" />
                    </div>
                    <div className="flex-grow flex flex-col justify-between" data-id="element-279">
                      <div data-id="element-280">
                        <div className="flex justify-between items-start mb-2" data-id="element-281">
                          <div data-id="element-282">
                            <h3 className="font-bold text-gray-900 text-lg mb-1" data-id="element-283">
                              {item.name}
                            </h3>
                            <div className="flex gap-2" data-id="element-284">
                              {item.isWIC && <TrustBadge type="WIC" size="sm" data-id="element-285" />}
                              {item.isEBT && <TrustBadge type="EBT" size="sm" data-id="element-286" />}
                            </div>
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-red-500 p-2 transition-colors" aria-label="Remove item" data-id="element-287">
                            <Trash2 className="w-5 h-5" data-id="element-288" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4" data-id="element-289">
                        <span className="font-bold text-[#1D3557] text-xl" data-id="element-290">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                        <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-2" data-id="element-291">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-9 h-9 flex items-center justify-center bg-white rounded-lg shadow-sm text-gray-600 font-bold hover:bg-gray-100 transition-colors" aria-label="Decrease quantity" data-id="element-292">
                            -
                          </button>
                          <span className="text-base font-semibold w-8 text-center" data-id="element-293">
                            {item.quantity}
                          </span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-9 h-9 flex items-center justify-center bg-white rounded-lg shadow-sm text-gray-600 font-bold hover:bg-gray-100 transition-colors" aria-label="Increase quantity" data-id="element-294">
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </div>

            <div className="lg:col-span-1" data-id="element-295">
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 sticky top-24" data-id="element-296">
                <h2 className="font-bold text-gray-900 text-2xl mb-6" data-id="element-297">
                  Order Summary
                </h2>
                <div className="space-y-4 text-base mb-6" data-id="element-298">
                  <div className="flex justify-between text-gray-600" data-id="element-299">
                    <span data-id="element-300">Subtotal</span>
                    <span className="font-semibold" data-id="element-301">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600" data-id="element-302">
                    <span data-id="element-303">Delivery</span>
                    {deliveryFee === 0 ? <span className="text-[#06A77D] font-bold" data-id="element-304">FREE</span> : <span className="font-semibold" data-id="element-305">
                        ${deliveryFee.toFixed(2)}
                      </span>}
                  </div>
                  {ebtTotal > 0 && <div className="flex justify-between text-[#06A77D] bg-green-50 p-3 rounded-lg" data-id="element-306">
                      <span className="font-semibold" data-id="element-307">EBT Eligible Amount</span>
                      <span className="font-bold" data-id="element-308">${ebtTotal.toFixed(2)}</span>
                    </div>}
                </div>
                <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-309">
                  <div className="flex justify-between items-end" data-id="element-310">
                    <span className="font-bold text-gray-900 text-lg" data-id="element-311">
                      Total
                    </span>
                    <span className="font-bold text-gray-900 text-3xl" data-id="element-312">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
                <button className="w-full bg-[#D37D29] text-white font-bold py-4 rounded-xl shadow-lg hover:bg-[#B86A23] transition-colors flex items-center justify-center gap-2 mb-4" data-id="element-313">
                  <ShieldCheck className="w-5 h-5" data-id="element-314" />
                  Secure Checkout
                </button>
                <div className="flex justify-center pt-4 border-t border-gray-100" data-id="element-315">
                  <TrustBadge type="USDA" data-id="element-316" />
                </div>
              </div>
            </div>
          </div> : <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-317">
            <div className="w-32 h-32 bg-amber-50 rounded-full flex items-center justify-center mb-8" data-id="element-318">
              <span className="text-6xl" data-id="element-319">🛒</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-320">
              Your cart is empty
            </h2>
            <p className="text-gray-500 mb-10 max-w-md text-lg" data-id="element-321">
              Looks like you haven't added any groceries yet. Start shopping to
              fill your pantry!
            </p>
            <Link to="/" className="bg-[#D37D29] text-white px-10 py-4 rounded-xl font-bold shadow-lg hover:bg-[#B86A23] transition-colors text-lg" data-id="element-322">
              Start Shopping
            </Link>
          </div>}
      </main>
    </div>;
}